import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LOCALES, localizePath, type Locale } from "@/hooks/useLocale";

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const switchLanguage = (lang: Locale) => {
    i18n.changeLanguage(lang);
    navigate(localizePath(location.pathname, lang) + location.search + location.hash);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className="ri-global-line text-base"></i>
        </span>
        <span>{t(`languages.${i18n.language}`)}</span>
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? "rotate-180" : ""}`}></i>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1">
          {SUPPORTED_LOCALES.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => switchLanguage(lang)}
              className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-2 text-sm cursor-pointer transition-colors hover:bg-background-100 ${
                i18n.language === lang ? "font-semibold text-primary-700" : "text-foreground-700"
              }`}
            >
              <span>{t(`languages.${lang}`)}</span>
              {i18n.language === lang && (
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-check-line text-sm"></i>
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}